// src/lib/trackEvent.js

import { api } from "./api";

const VISITOR_ID_KEY = "fq_visitor_id";
const SESSION_ID_KEY = "fq_session_id";
const SESSION_STARTED_AT_KEY = "fq_session_started_at";
const ATTRIBUTION_KEY = "fq_attribution";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function createId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getVisitorId() {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);

  if (!visitorId) {
    visitorId = createId();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  }

  return visitorId;
}

function getSessionId() {
  const now = Date.now();

  const storedSessionId =
    sessionStorage.getItem(SESSION_ID_KEY);

  const storedStartedAt = Number(
    sessionStorage.getItem(
      SESSION_STARTED_AT_KEY
    )
  );

  const sessionExpired =
    !storedStartedAt ||
    now - storedStartedAt >
      SESSION_TIMEOUT_MS;

  if (!storedSessionId || sessionExpired) {
    const sessionId = createId();

    sessionStorage.setItem(
      SESSION_ID_KEY,
      sessionId
    );

    sessionStorage.setItem(
      SESSION_STARTED_AT_KEY,
      String(now)
    );

    return sessionId;
  }

  sessionStorage.setItem(
    SESSION_STARTED_AT_KEY,
    String(now)
  );

  return storedSessionId;
}

function getStoredAttribution() {
  try {
    const stored =
      localStorage.getItem(ATTRIBUTION_KEY);

    return stored
      ? JSON.parse(stored)
      : null;
  } catch (err) {
    console.error(
      "Unable to read FlowQuant attribution:",
      err
    );

    return null;
  }
}

function captureFirstTouchAttribution() {
  const existing = getStoredAttribution();

  const params = new URLSearchParams(
    window.location.search
  );

  const currentCampaign = {
    utm_source:
      params.get("utm_source") || null,

    utm_medium:
      params.get("utm_medium") || null,

    utm_campaign:
      params.get("utm_campaign") || null,

    utm_content:
      params.get("utm_content") || null,

    utm_term:
      params.get("utm_term") || null,

    fbclid:
      params.get("fbclid") || null,
  };

  const hasCurrentCampaign = Boolean(
    currentCampaign.utm_source ||
      currentCampaign.utm_medium ||
      currentCampaign.utm_campaign ||
      currentCampaign.utm_content ||
      currentCampaign.utm_term ||
      currentCampaign.fbclid
  );

  const hasExistingCampaign = Boolean(
    existing?.utm_source ||
      existing?.utm_medium ||
      existing?.utm_campaign ||
      existing?.utm_content ||
      existing?.utm_term ||
      existing?.fbclid
  );

  if (existing && hasExistingCampaign) {
    return existing;
  }

  if (existing && !hasCurrentCampaign) {
    return existing;
  }

  const attribution = {
    ...currentCampaign,

    referrer:
      document.referrer ||
      existing?.referrer ||
      null,

    landing_path:
      `${window.location.pathname}${window.location.search}`,

    first_touch_at:
      existing?.first_touch_at ||
      new Date().toISOString(),
  };

  localStorage.setItem(
    ATTRIBUTION_KEY,
    JSON.stringify(attribution)
  );

  return attribution;
}

function getAnalyticsContext() {
  const attribution =
    captureFirstTouchAttribution();

  return {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    ...attribution,
  };
}

export async function trackEvent(
  eventName,
  module,
  metadata = {}
) {
  try {
    if (!eventName) {
      return;
    }

    const analyticsContext =
      getAnalyticsContext();

    await api("/events", {
      method: "POST",

      body: JSON.stringify({
        owner_type: "anonymous",

        owner_id:
          analyticsContext.visitor_id,

        event_name: eventName,

        module:
          module || null,

        path:
          window.location.pathname,

        metadata: {
          ...analyticsContext,
          ...metadata,
        },
      }),
    });
  } catch (err) {
    console.error(
      "trackEvent failed:",
      err
    );
  }
}